import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AlquileresContext } from "../../../../context/AlquileresContext";
import { ActividadContext } from "../../../../context/ActividadContext";
import { saveContrato } from "../../../../application/contratos";
import {
  calcularComision,
  calcularTotalPropietario,
  calcularProximoAumento,
} from "../../../../utils/rentalCalculations";
import QuickAddLocador from "../../../../components/QuickAddLocador/QuickAddLocador";
import QuickAddLocatario from "../../../../components/QuickAddLocatario/QuickAddLocatario";
import SearchableSelect from "../../../../components/SearchableSelect";
import MoneyInput from "../../../../components/MoneyInput";
import { nombreCompleto } from "../../../../utils/nombre";
import { FaArrowLeft, FaPlusCircle } from "react-icons/fa";

function ContratoCreate() {
  const navigate = useNavigate();
  const { locadores, locatarios, config, refreshContratos, refreshLocadores, refreshLocatarios } =
    useContext(AlquileresContext);
  const { registrarActividad } = useContext(ActividadContext);

  const [showAddLocador, setShowAddLocador] = useState(false);
  const [showAddLocatario, setShowAddLocatario] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    locadorId: "",
    locadorNombre: "",
    locatarioId: "",
    locatarioNombre: "",
    propiedad: "",
    montoActual: "",
    moneda: "ARS",
    comisionPorcentaje: config.comisionPorcentaje || 5,
    comisionMonto: 0,
    totalPropietario: 0,
    fechaInicio: "",
    fechaVencimiento: "",
    valorInicial: "",
    aumento: {
      tipo: "IPC",
      frecuencia: "TRIMESTRAL",
      proximoAumento: "",
      fechaUltimoAumento: "",
    },
    estado: "ACTIVO",
    observaciones: "",
  });

  useEffect(() => {
    setForm((prev) => ({ ...prev, comisionPorcentaje: config.comisionPorcentaje || 5 }));
  }, [config]);

  // Auto-calculate comision
  useEffect(() => {
    const monto = Number(form.montoActual) || 0;
    const porc = Number(form.comisionPorcentaje) || 0;
    const comision = calcularComision(monto, porc);
    const total = calcularTotalPropietario(monto, comision);
    setForm((prev) => ({ ...prev, comisionMonto: comision, totalPropietario: total }));
  }, [form.montoActual, form.comisionPorcentaje]);

  // Auto-calculate proximo aumento
  useEffect(() => {
    if (form.fechaInicio && form.aumento.frecuencia) {
      const proximo = calcularProximoAumento(form.fechaInicio, form.aumento.frecuencia);
      setForm((prev) => ({
        ...prev,
        aumento: { ...prev.aumento, proximoAumento: proximo, fechaUltimoAumento: form.fechaInicio },
      }));
    }
  }, [form.fechaInicio, form.aumento.frecuencia]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAumento = (field, value) => {
    setForm((prev) => ({
      ...prev,
      aumento: { ...prev.aumento, [field]: value },
    }));
  };

  const handleLocadorChange = (id) => {
    const loc = locadores.find((l) => l.idFirebase === id);
    setForm((prev) => ({
      ...prev,
      locadorId: id,
      locadorNombre: loc ? nombreCompleto(loc) : "",
    }));
  };

  const handleLocatarioChange = (id) => {
    const loc = locatarios.find((l) => l.idFirebase === id);
    setForm((prev) => ({
      ...prev,
      locatarioId: id,
      locatarioNombre: loc ? nombreCompleto(loc) : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        montoActual: Number(form.montoActual),
        valorInicial: Number(form.valorInicial) || Number(form.montoActual),
        comisionPorcentaje: Number(form.comisionPorcentaje),
        comisionMonto: Number(form.comisionMonto),
        totalPropietario: Number(form.totalPropietario),
      };
      await saveContrato(data);
      await refreshContratos();
      registrarActividad("CONTRATO_CREADO", {
        mensaje: `Contrato creado: ${data.locatarioNombre} - ${data.propiedad}`,
        entidadTipo: "CONTRATO",
        entidadLabel: data.propiedad,
        monto: data.montoActual,
        moneda: data.moneda,
      });
      navigate("/dashboard/alquileres/contratos");
    } catch (error) {
      console.error("Error al guardar contrato:", error);
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";

  return (
    <div className="px-4 md:px-6 py-4 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <FaArrowLeft />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
          Nuevo Contrato
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Partes */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Partes del Contrato
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Locador *</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    options={locadores}
                    value={form.locadorId}
                    onChange={handleLocadorChange}
                    getLabel={nombreCompleto}
                    placeholder="Buscar locador..."
                    required
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddLocador(true)}
                  className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  title="Agregar locador"
                >
                  <FaPlusCircle className="text-primary w-4 h-4" />
                </button>
              </div>
            </div>
            <div>
              <label className={labelClass}>Locatario *</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    options={locatarios}
                    value={form.locatarioId}
                    onChange={handleLocatarioChange}
                    getLabel={nombreCompleto}
                    placeholder="Buscar locatario..."
                    required
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddLocatario(true)}
                  className="px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  title="Agregar locatario"
                >
                  <FaPlusCircle className="text-primary w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
          <div>
            <label className={labelClass}>Propiedad *</label>
            <input
              type="text"
              value={form.propiedad}
              onChange={(e) => updateField("propiedad", e.target.value)}
              className={inputClass}
              placeholder="Direccion o descripcion de la propiedad"
              required
            />
          </div>
        </section>

        {/* Datos Economicos */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Datos Economicos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Monto Mensual *</label>
              <MoneyInput
                value={form.montoActual}
                onChange={(v) => updateField("montoActual", v)}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Moneda</label>
              <select
                value={form.moneda}
                onChange={(e) => updateField("moneda", e.target.value)}
                className={inputClass}
              >
                <option value="ARS">ARS (Pesos)</option>
                <option value="USD">USD (Dolares)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Comision %</label>
              <input
                type="number"
                value={form.comisionPorcentaje}
                onChange={(e) => updateField("comisionPorcentaje", e.target.value)}
                className={inputClass}
                min="0"
                max="100"
                step="0.5"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Comision</p>
              <p className="text-lg font-bold text-primary">
                {form.moneda === "USD" ? "U$D" : "$"}{" "}
                {Number(form.comisionMonto).toLocaleString("es-AR")}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">Total Propietario</p>
              <p className="text-lg font-bold text-green-600 dark:text-green-400">
                {form.moneda === "USD" ? "U$D" : "$"}{" "}
                {Number(form.totalPropietario).toLocaleString("es-AR")}
              </p>
            </div>
          </div>
        </section>

        {/* Fechas */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Fechas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Fecha de Inicio *</label>
              <input
                type="date"
                value={form.fechaInicio}
                onChange={(e) => updateField("fechaInicio", e.target.value)}
                className={inputClass}
                required
              />
            </div>
            <div>
              <label className={labelClass}>Fecha de Vencimiento</label>
              <input
                type="date"
                value={form.fechaVencimiento}
                onChange={(e) => updateField("fechaVencimiento", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </section>

        {/* Aumentos */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Aumentos
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Tipo de Aumento</label>
              <select
                value={form.aumento.tipo}
                onChange={(e) => updateAumento("tipo", e.target.value)}
                className={inputClass}
              >
                <option value="IPC">IPC</option>
                <option value="INDEX">INDEX</option>
                <option value="FIJO">FIJO</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Frecuencia</label>
              <select
                value={form.aumento.frecuencia}
                onChange={(e) => updateAumento("frecuencia", e.target.value)}
                className={inputClass}
              >
                <option value="TRIMESTRAL">Trimestral</option>
                <option value="CUATRIMESTRAL">Cuatrimestral</option>
                <option value="SEMESTRAL">Semestral</option>
                <option value="ANUAL">Anual</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Proximo Aumento</label>
              <input
                type="date"
                value={form.aumento.proximoAumento}
                onChange={(e) => updateAumento("proximoAumento", e.target.value)}
                className={inputClass}
              />
            </div>
          </div>
        </section>

        {/* Estado y Observaciones */}
        <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 pb-2">
            Estado y Observaciones
          </h2>
          <div>
            <label className={labelClass}>Estado</label>
            <select
              value={form.estado}
              onChange={(e) => updateField("estado", e.target.value)}
              className={inputClass}
            >
              <option value="ACTIVO">Activo</option>
              <option value="VENCIDO">Vencido</option>
              <option value="RESCINDIDO">Rescindido</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Observaciones</label>
            <textarea
              value={form.observaciones}
              onChange={(e) => updateField("observaciones", e.target.value)}
              className={`${inputClass} h-24 resize-none`}
              placeholder="Notas adicionales..."
            />
          </div>
        </section>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 text-sm bg-primary hover:bg-secondary text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? "Guardando..." : "Guardar Contrato"}
          </button>
        </div>
      </form>

      <QuickAddLocador
        isOpen={showAddLocador}
        onClose={() => setShowAddLocador(false)}
        onCreated={(loc) => {
          refreshLocadores();
          handleLocadorChange(loc.idFirebase);
        }}
      />
      <QuickAddLocatario
        isOpen={showAddLocatario}
        onClose={() => setShowAddLocatario(false)}
        onCreated={(loc) => {
          refreshLocatarios();
          handleLocatarioChange(loc.idFirebase);
        }}
      />
    </div>
  );
}

export default ContratoCreate;
